import { useLocation, useParams } from 'react-router-dom';
import instance from '../config/axios';
import { useEffect, useState } from 'react';
import { Product, ROLE } from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import Loading from '../components/Loading';
import ProductCarousel from '../components/ProductCarousel';
import RenderProductDescription from '../components/RenderProductDescription';
import renderMediaUrls from '../utils/getMediaUrls';
import GuestAddToCartButton from '../components/GuestAddToCartButton';
import { GuestCartProvider } from '../context/guestCartContext';
import CustomModal from '../components/CustomModal';
import { MAX_ZOOM, MIN_ZOOM, ZOOM_STEP } from '../constant/constant';
import useHandleZoom from '../hooks/usehandleZoom';
import ProductDescriptionTabs from './ProductDescriptionTab.tsx';
import { CheckIconNew, OutOfStockIcon } from '../assets/svgImages';
import Accordion from './Accordion.tsx';
import GuestSimilarProducts from './guest/GuestSimilarProducts.tsx';
import Breadcrumbs from './Breadcrumbs.tsx';
import { useQuery } from '@tanstack/react-query';
import ShopifyRichText from './RenderRichTextContent.tsx';
import { parseJson } from '../utils/parseJson.ts';

const GuestProductDetails = ({ isGuest = false }: any) => {
  const { productId } = useParams();
  const location = useLocation();
  const [open, setOpen] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [zoom, setZoom] = useState<number>(MIN_ZOOM);
  const {
    handleTouchMove,
    handleTouchStart,
    position,
    setPosition,
    setDragging,
  } = useHandleZoom(zoom);
  const { user } = useAuth();
  const isPatient = (user?.profile?.groups as Array<string>)?.includes(
    ROLE.Patients,
  );
  const [openIndex, setOpenIndex] = useState<number | null>();

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    setOpenIndex(isMobile ? 0 : null);
  }, []);

  const handleToggle = (index: number) => {
    if (openIndex === index) {
      setOpenIndex(null);
    } else {
      setOpenIndex(index);
    }
  };

  const getSameCategoryProducts = async (categoryId: number) => {
    const response = await instance.get(
      `guest/similar-products/${categoryId}`,
      { headers: {} },
    );
    return response.data;
  };

  const fetchProductDetails = async (id: string) => {
    const endpoint = `/guest/product/${id}`;
    const response = await instance.get(endpoint);
    return response.data;
  };

  const { data: product, isLoading } = useQuery({
    queryKey: ['product-details', productId],
    queryFn: () => fetchProductDetails(productId as string),
    enabled: !!productId,
  });

  const { data: sameCategoryProducts, isLoading: sameCategoryLoading } =
    useQuery({
      queryKey: ['same-category-products', product?.productCategory?.id],
      queryFn: () => getSameCategoryProducts(product!.productCategory.id),
      enabled: !!product?.productCategory?.id,
    });

  const direction = parseJson(product?.direction);

  const tabs = [
    {
      key: 'tab1',
      label: 'General Information',
      content: (
        <div className="text-sm text-gray-700">
          <RenderProductDescription description={product?.description} />
        </div>
      ),
    },
    {
      key: 'tab2',
      label: 'Ingredients',
      content: (
        <div className="my-3">
          {product?.ingredients ? (
            <ShopifyRichText value={JSON.parse(product.ingredients)} />
          ) : (
            <div className="text-sm font-regular text-primary-blue-500 text-left">
              No Ingredients data available
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'tab3',
      label: 'Directions',
      content: (
        <div className="my-3">
          {direction ? (
            <div className="text-sm font-regular text-left">
              <ShopifyRichText value={direction} />
            </div>
          ) : (
            <div className="text-sm font-regular text-primary-blue-500 text-left">
              No Directions data available
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'tab4',
      label: 'Warnings',
      content: (
        <div className="my-3">
          {product?.warnings ? (
            <ShopifyRichText value={JSON.parse(product.warnings)} />
          ) : (
            <div className="text-sm font-regular text-primary-blue-500 text-left">
              No warnings data available
            </div>
          )}
        </div>
      ),
    },
  ];

  if (isLoading || sameCategoryLoading) return <Loading />;

  return (
    <div className="mx-auto container-compact">
      <Breadcrumbs
        data={[
          {
            tag: 'a',
            href: '/guest/shop',
            children: <>Home</>,
          },
          {
            tag: 'p',
            children: <span>Product Details</span>,
          },
        ]}
      />{' '}
      {product ? (
        <>
          <div className="bg-white rounded-lg border border-grey-200 shadow-normal p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
              <div className="w-full md:w-1/2 flex justify-center self-start md:sticky top-4">
                <ProductCarousel
                  media={renderMediaUrls(product)}
                  setImageUrl={setImageUrl}
                  setOpen={setOpen}
                />
              </div>

              <div className="w-full md:w-1/2 flex flex-col">
                <h1 className="text-xl md:text-2xl font-semibold text-primary-blue-500 leading-tight mb-3">
                  {product.name}
                </h1>
                <div className="flex flex-wrap gap-2 mb-6">
                  {product.productCategory && (
                    <span className="px-3 py-1 rounded-full uppercase text-xs text-white font-normal bg-secondary-pink-500">
                      {product.productCategory.name}
                    </span>
                  )}
                  {product.productFormulation && (
                    <span className="px-3 py-1 rounded-full uppercase text-xs text-white font-normal bg-secondary-green-600 whitespace-nowrap">
                      {product.productFormulation.name}
                    </span>
                  )}
                </div>
                <hr className="border-gray-100 mb-4 md:mb-6 2xl:mb-8" />
                <div className="mb-6 md:mb-8 xl:mb-10">
                  <div className="flex flex-wrap items-baseline gap-2">
                    {product?.old_price ? (
                      <span className="text-sm font-semibold text-primary-blue-500 line-through">
                        ${product?.old_price}
                      </span>
                    ) : (
                      <></>
                    )}
                    <span className="text-2xl sm:text-3xl font-semibold text-primary-blue-500">
                      ${product?.price}
                    </span>
                  </div>
                  {product.inventory_quantity > 0 ? (
                    <div className="flex items-center">
                      <CheckIconNew className="w-5 h-5" />
                      <p className="ms-1 text-xs font-semibold text-secondary-green-600">
                        In stock and ready to ship
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <OutOfStockIcon className="w-5 h-5 fill-secondary-pink-500" />
                      <p className="ms-1 text-xs font-semibold text-secondary-pink-500">
                        Product is Out of Stock
                      </p>
                    </div>
                  )}
                </div>

                <div className="w-full xl:w-8/12 rounded-xl">
                  <GuestCartProvider>
                    <GuestAddToCartButton
                      product={product}
                      substituteProduct={product.substituteProduct}
                    />
                  </GuestCartProvider>
                </div>
                {!isPatient && !isGuest && (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold text-primary-blue-500">
                      Stock on hand
                    </p>
                    <p className="text-sm text-gray-700">
                      {product?.inventory_quantity ?? '-'} units available
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 md:mt-8 hidden md:block">
              <ProductDescriptionTabs
                tabs={tabs}
                isLoading={isLoading || sameCategoryLoading}
              />
            </div>

            <div className="mt-6 md:mt-8 md:hidden block ">
              {tabs?.map((tab, index) => (
                <Accordion
                  key={tab.key}
                  title={
                    <p className="text-sm font-semibold text-primary-blue-400">
                      {tab.label}
                    </p>
                  }
                  isOpen={openIndex === index}
                  onAccordionToggle={() => handleToggle(index)}
                >
                  <>{tab.content}</>
                </Accordion>
              ))}
            </div>

            <CustomModal
              isOpen={open}
              onClose={() => {
                setOpen(false);
                setZoom(MIN_ZOOM);
                setPosition({ x: 0, y: 0 });
              }}
              header=""
              footer=""
              modalSize="w-full h-full sm:w-auto sm:h-auto flex justify-center md:hidden block"
            >
              <div className="relative w-full max-w-full max-h-[80vh] overflow-hidden flex items-center justify-center">
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="object-contain max-w-full max-h-[80vh] transition-transform duration-200"
                  style={{
                    transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
                    touchAction: 'none',
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={() => setDragging(false)}
                />
                <div className="absolute bottom-3 right-3 flex flex-row bg-white/90 rounded shadow">
                  <button
                    type="button"
                    className="p-2 disabled:opacity-40"
                    onClick={() => {
                      setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
                      setPosition({ x: 0, y: 0 });
                    }}
                    disabled={zoom <= MIN_ZOOM}
                  >
                    −
                  </button>
                  <button
                    type="button"
                    className="p-2 border-b disabled:opacity-40"
                    onClick={() =>
                      setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM))
                    }
                    disabled={zoom >= MAX_ZOOM}
                  >
                    +
                  </button>
                </div>
              </div>
            </CustomModal>
          </div>
          {sameCategoryProducts?.length ? (
            <GuestCartProvider>
              <section className="mt-8 md:mt-10 lg:mt-14">
                <GuestSimilarProducts
                  products={sameCategoryProducts?.filter(
                    (pro: Product) => pro.id !== product.id,
                  )}
                  isGuest={true}
                  addCart={location?.state?.addCart}
                  removeProductFromCart={location?.state?.removeProductFromCart}
                />
              </section>
            </GuestCartProvider>
          ) : (
            <></>
          )}
        </>
      ) : (
        <h3 className="text-base font-semibold text-center leading-7 text-gray-900 px-4">
          Product Information does not exist
        </h3>
      )}
    </div>
  );
};

export default GuestProductDetails;
